/**
 * Campaign Manager Factory
 *
 * Factory for creating and managing campaign manager instances.
 * Provides singleton-like behavior per componentId to support multiple
 * concurrent campaigns while avoiding duplicate instances.
 */

import { CampaignStateManager } from './CampaignStateManager';
import { CampaignConfig } from '../types/CampaignTypes';

/**
 * Factory for creating and managing campaign manager instances.
 * Maintains one manager instance per unique componentId.
 *
 * @example
 * const config: CampaignConfig<ActionId> = {
 *     componentId: 'trial-countdown',
 *     requiredActions: [ActionId.Action1, ActionId.Action2],
 *     actionMapper: (id) => mapActionId(id)
 * };
 *
 * const manager = CampaignManagerFactory.getManager(config);
 */
export class CampaignManagerFactory {
    /**
     * Map of campaign instances keyed by componentId.
     * Supports multiple campaigns with different configurations.
     */
    private static instances: Map<string, CampaignStateManager<string | number>> = new Map();

    /**
     * Gets or creates a campaign manager for the given configuration.
     * Maintains one instance per componentId for efficient memory usage.
     *
     * @template TActionId - The action ID type for this campaign
     * @param config - Campaign configuration defining actions, mapping, and completion logic
     * @returns Campaign manager instance for this campaign
     *
     * @example
     * // First call creates instance
     * const manager1 = CampaignManagerFactory.getManager(config);
     *
     * // Subsequent calls with same componentId return same instance
     * const manager2 = CampaignManagerFactory.getManager(config);
     * // manager1 === manager2 (true)
     */
    static getManager<TActionId extends string | number>(
        config: CampaignConfig<TActionId>
    ): CampaignStateManager<TActionId> {
        let manager = this.instances.get(config.componentId);
        if (!manager) {
            manager = new CampaignStateManager(config);
            this.instances.set(config.componentId, manager);
        }

        return manager as CampaignStateManager<TActionId>;
    }

    /**
     * Clears a specific campaign manager instance.
     * Useful for testing or resetting a specific campaign.
     *
     * @param componentId - The component ID to reset
     *
     * @example
     * CampaignManagerFactory.resetManager('trial-countdown');
     */
    static resetManager(componentId: string): void {
        this.instances.delete(componentId);
    }

    /**
     * Clears all campaign manager instances.
     * Useful for testing or application reset scenarios.
     *
     * @example
     * // Clear all campaigns (e.g., on logout)
     * CampaignManagerFactory.resetAll();
     */
    static resetAll(): void {
        this.instances.clear();
    }

    /**
     * Gets the number of active campaign manager instances.
     * Useful for debugging and monitoring.
     *
     * @returns Number of active campaigns
     */
    static getActiveCount(): number {
        return this.instances.size;
    }

    /**
     * Checks if a campaign manager exists for the given ID.
     *
     * @param componentId - The component ID to check
     * @returns true if manager exists
     */
    static hasManager(componentId: string): boolean {
        return this.instances.has(componentId);
    }
}
